import { ActivatedRoute, Router } from '@angular/router';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// App Services
import { AuthService } from '@app/shared/services/auth.service';
import { AuthUser } from '@app/account/account-api.model';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { UserGroupService } from '@app/shared/services/user-group.service';
import { GroupService } from '@app/shared/services/group.service';

// Utils / Tokens
import { ProfileService } from '@app/profile/services/profile.service';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { Group } from '@app/groups/group-api';
import { LearnerHomeTrackerService } from '@app/learner-home/services/learner-home-tracker.service';

// Exports
export interface Profile {
  id: number | string; // This can either be a numeric key or a string uuid, beware!
  pic?: string;
  name: string;
  nameEncoded?: string;
  bio?: string;
  isEngaged: boolean;
}
export interface GroupPermissions {
  canCreateAdministrativeGroups: boolean;
  canCreateClosedGroups: boolean;
  canCreateGroups: boolean;
  canCreateOpenGroups: boolean;
  canCreatePrivateGroups: boolean;
  isGroupsVisible: boolean;
  isRegulated: boolean;
}

@Component({
  selector: 'dgx-profile-groups',
  templateUrl: './profile-groups.component.html',
})
export class ProfileGroupsComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public readonly CLASS_NAME = 'ProfileGroupsComponent';

  @Input() public owner: Profile;

  // Groups
  public groups = [];

  // User info
  public authUser: AuthUser = this.auth.authUser;
  public orgId: number = this.authUser?.defaultOrgInfo?.organizationId;
  public permissions: GroupPermissions;
  // Display props
  public groupLimit = 21; // Divisible by 3
  public groupOffset = 0;
  public loadingGroups: boolean;
  public isLoading: boolean;
  public moreGroupsAvailable: boolean;

  public groupsHeader = this.translate.instant('Core_Groups');

  public i18n = this.translate.instant([
    'Core_Browse',
    'Core_Create',
    'ProfileCtrl_GroupsDescription',
    'Core_Empty_Image_Alt',
    'GroupCtrl_EmptyStateTitle',
    'GroupCtrl_EmptyStateDescription',
    'A11y_BrowseGroups',
    'A11y_CreateGroup',
  ]);

  public isMobile: boolean;

  constructor(
    private auth: AuthService,
    private group: GroupService,
    private translate: TranslateService,
    private notifier: NotifierService,
    private tracker: TrackerService,
    private userGroup: UserGroupService,
    private router: Router,
    private route: ActivatedRoute,
    private profileService: ProfileService,
    private windowLayoutService: WindowLayoutService,
    private cdr: ChangeDetectorRef,
    private learnerHomeTrackerService: LearnerHomeTrackerService
  ) {
    super();
  }

  public get hasGroupCreatePermissions(): boolean {
    return (
      this.permissions?.canCreateGroups ||
      this.permissions?.canCreateAdministrativeGroups // Open, closed, private
    );
  }

  public goBrowseGroups(): void {
    this.router.navigateByUrl('/learning/groups');
  }

  public addGroup(event: Event): void {
    // show new group settings
    this.group
      .addGroup({
        orgId: this.orgId,
        ...this.permissions,
      })
      .subscribe((groupId: any) => {
        // Redirect to new group on profile.
        if (groupId) {
          this.router.navigate([`/groups/${groupId}`]);
        }
      });
  }

  public loadAllGroups(append: boolean): void {
    if (append) {
      this.groupOffset += this.groupLimit;
    } else {
      this.groupOffset = 0;
      this.groups = [];
    }

    if (this.authUser) {
      this.loadingGroups = true;
      this.userGroup
        .getGroupsWithTopActiveMembers(
          this.owner.id,
          this.groupOffset,
          this.groupLimit
        )
        .subscribe({
          next: (response: any) => {
            const data = response;
            if (append) {
              this.groups.push.apply(this.groups, data.groups);
            } else {
              this.groups = data.groups;
            }
            this.moreGroupsAvailable = data.hasMoreItems;
            this.loadingGroups = false;
            this.cdr.detectChanges();
          },
          complete: () => {
            this.loadingGroups = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.notifier.showError(
              this.translate.instant('UserGroupsSvc_ProblemAccessingGroup')
            );
          },
        });
    }
  }

  public ngOnInit(): void {
    this.isMobile = this.windowLayoutService.isMobile;
    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });

    this.isLoading = true;
    this.group.getUserGroupPermissions().subscribe((permissions) => {
      this.permissions = permissions;
      this.loadAllGroups(false);
      this.isLoading = false;
    });
    this.tracker.trackEventData({
      action: 'Profile Groups Viewed',
      properties: {
        UserId: this.owner.id,
        IsActiveLearner: this.owner.isEngaged,
        ProfileType: this.profileService.getProfileType(),
      },
    });
  }

  public onIntersectionOfGroup(group: Group, index: number): void {
    const properties = {
      location: 'Learner Home', // Maps to ActionLocation in Amplitude
      inputType: 'Group', // Maps to ContentType in Amplitude
      groupId: group.groupId,
      groupName: group.name,
      position: index,
    };
    this.learnerHomeTrackerService.trackContentImpressed(properties);
  }
}
